// AuthProvider.jsx
import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { msalConfig } from "./entraauthConfig";

const msalInstance = new PublicClientApplication(msalConfig); // MUST be outside component
const ACTIVE_ACCOUNT_STORAGE_KEY = "msal_active_account_home_id";

let msalInitPromise = null;
let redirectPromise = null;

const persistActiveAccount = (account) => {
  if (!account?.homeAccountId) {
    return;
  }
  localStorage.setItem(ACTIVE_ACCOUNT_STORAGE_KEY, account.homeAccountId);
};

const restoreActiveAccount = () => {
  const savedHomeAccountId = localStorage.getItem(ACTIVE_ACCOUNT_STORAGE_KEY);
  if (!savedHomeAccountId) {
    return null;
  }

  const savedAccount = msalInstance.getAccount({ homeAccountId: savedHomeAccountId });
  if (savedAccount) {
    msalInstance.setActiveAccount(savedAccount);
    return savedAccount;
  }

  return null;
};


export async function initializeMsal() {
  if (!msalInitPromise) {
    msalInitPromise = msalInstance.initialize();
  }

  await msalInitPromise;

  if (!redirectPromise) {
    console.log("[MSAL] handleRedirectPromise: start");
    redirectPromise = msalInstance
      .handleRedirectPromise()
      .then((response) => {
        console.log("[MSAL] handleRedirectPromise: response", response ? "present" : "null");

        if (response?.account) {
          msalInstance.setActiveAccount(response.account);
          persistActiveAccount(response.account);
          console.log("[MSAL] active account set from redirect response", response.account.username);
          return;
        }

        const restoredAccount = restoreActiveAccount();
        if (restoredAccount) {
          console.log("[MSAL] active account restored from storage", restoredAccount.username);
          return;
        }

        const accounts = msalInstance.getAllAccounts();
        console.log("[MSAL] getAllAccounts count", accounts.length);
        if (!msalInstance.getActiveAccount() && accounts.length > 0) {
          msalInstance.setActiveAccount(accounts[0]);
          persistActiveAccount(accounts[0]);
          console.log("[MSAL] active account set from cached accounts", accounts[0].username);
        }
      })
      .catch((error) => {
        console.error("[MSAL] handleRedirectPromise failed", error);
        throw error;
      });
  }

  await redirectPromise;
}


export default function AuthProvider({ children }) {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    let isMounted = true;

    initializeMsal()
      .then(() => {
        if (isMounted) {
          setIsReady(true);
        }
      })
      .catch((error) => {
        console.error("[MSAL] initializeMsal failed", error);
        if (isMounted) {
          setIsReady(true);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  if (!isReady) {
    return null;
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export { msalInstance };
